// src/cpq_agent/webchat_ui/agent-activity.js
//
// Renders the live agent UI inside an assistant chat bubble — status badge,
// inline tool-call cards, streaming text, markdown summary, action buttons.
// Modeled on Claude desktop / Codex desktop conventions.

import { renderMarkdown, renderInline } from "./agent-parts-md.js";
import { getAuthMode } from "./auth.js";
import { wsSend } from "./ws.js";
import { shouldRenderSummaryFallback } from "./agent-finalize-helpers.js";

const STATE = new Map();
// conversation_id -> {
//   bubble: <chat-msg el>,
//   body:   <msg-prose el>,
//   process:      <.agent-process el — collapsible Process block (reasoning + tools)>,
//   processHead:  <.process-head el — the only top-level disclosure toggle>,
//   processIcon:  <i.ic el inside processHead (loader while live, cpu once done)>,
//   processLabel: <span.process-label el inside processHead>,
//   processBody:  <.process-body el — non-text render-items diff into here>,
//   answer:       <.agent-answer el — text render-items diff into here>,
//   cancelLink:   <button el inside processHead | null after final>,
//   userToggled:   bool — sticky once the user manually opens/closes the Process block,
//   autoCollapsed: bool — true once auto-collapse has fired for this turn,
//   summary:<agent-summary el | null until final>,
//   actions:<agent-actions el | null until final>,
//   toolCalls: Map<call_id, { row, detail, head, checkEl, tool, startedAt }>,
//   startedAt: number, toolCount: number,
// }

function el(tag, attrs = {}, ...children) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") e.className = v;
    else if (k === "html") e.innerHTML = v;
    else if (k.startsWith("on") && typeof v === "function") e.addEventListener(k.slice(2), v);
    else if (v != null) e.setAttribute(k, v);
  }
  for (const c of children) {
    if (c == null || c === false) continue;
    e.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return e;
}

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
}

function shortToolName(tool) {
  if (!tool) return "";
  return tool.startsWith("mcp__") ? tool.split("__").slice(-1)[0] : tool;
}

const VERB_MAP = {
  Read: "Read",
  Write: "Wrote",
  Edit: "Edited",
  MultiEdit: "Edited",
  Glob: "Globbed",
  Grep: "Searched",
  Bash: "Ran",
  TodoWrite: "Tracked",
  WebFetch: "Fetched",
  WebSearch: "Searched",
  Task: "Delegated",
  Skill: "Used skill",
  ToolSearch: "Looked up",
};

const IRREGULAR_VERBS = {
  read: "Read",
  write: "Wrote",
  grep: "Grepped",
  lookup: "Looked up",
  run: "Ran",
  set: "Set",
};

function humanizeVerb(tool) {
  if (!tool) return "Ran";
  if (VERB_MAP[tool]) return VERB_MAP[tool];
  // MCP tools (mcp__agentcpq__script_validate_syntax → "Validated syntax")
  if (tool.startsWith("mcp__")) {
    const last = tool.split("__").slice(-1)[0]; // script_validate_syntax
    const words = last.split("_");               // ["script", "validate", "syntax"]
    // Drop a category prefix when present
    const generic = [
      "script", "sync", "configure", "quote", "template",
      "admin", "codebase", "document", "knowledge",
      "calc", "doc", "field", "formula", "global", "product", "user",
    ];
    if (words.length > 1 && generic.includes(words[0])) words.shift();
    // Secondary strip: skip leading nouns so the actual verb gets past-tensed.
    const secondaryNouns = ["file", "attrs", "introspect"];
    if (words.length > 1 && secondaryNouns.includes(words[0])) words.shift();
    // Past-tense the leading verb, using irregular map first.
    const v0 = words[0] || "ran";
    const past = IRREGULAR_VERBS[v0]
      ?? (v0.endsWith("e")
            ? v0.charAt(0).toUpperCase() + v0.slice(1) + "d"
            : v0.charAt(0).toUpperCase() + v0.slice(1) + "ed");
    return [past, ...words.slice(1)].join(" ");
  }
  // Fallback: title-case the tool name itself
  return tool.charAt(0).toUpperCase() + tool.slice(1);
}

function activityTarget(tool, args) {
  // Pick the most-meaningful single arg for the row's monospace target column.
  if (!args || typeof args !== "object") return "";
  if (args.file_path) return shortenPath(args.file_path);
  if (args.path) return shortenPath(args.path);
  if (args.pattern) return args.pattern;
  if (args.command) return args.command;
  if (args.query) return args.query;
  if (args.url) return args.url;
  // MCP tools: prefer name/id over the rest
  if (args.name) return args.name;
  if (args.id) return String(args.id);
  // Fall back to a one-line JSON dump
  try {
    const s = JSON.stringify(args);
    return s.length > 80 ? s.slice(0, 79) + "…" : s;
  } catch { return ""; }
}

// Strip the workspace prefix from absolute paths so the activity log shows
// `tenant/global-scripts/X.py` instead of
// `/Users/golub/.cpq-agent/workspaces/<id>/tenant/global-scripts/X.py`.
function shortenPath(p) {
  if (!p || typeof p !== "string") return p;
  // Match any ".cpq-agent/workspaces/<id>/" anywhere and keep what's after.
  const m = p.match(/\.cpq-agent\/workspaces\/[^/]+\/(.*)$/);
  if (m) return m[1];
  // Also collapse $HOME to ~ for any other absolute home paths.
  const home = "/Users/";
  if (p.startsWith(home)) {
    const rest = p.slice(home.length);
    const slash = rest.indexOf("/");
    return slash === -1 ? p : "~/" + rest.slice(slash + 1);
  }
  return p;
}

function summarizeArgs(tool, args) {
  if (!args || typeof args !== "object") return "";
  const short = shortToolName(tool);
  if (args.file_path) return shortenPath(args.file_path);
  if (args.path) return shortenPath(args.path);
  if (args.pattern && args.path) return `"${args.pattern}" in ${shortenPath(args.path)}`;
  if (args.pattern) return `"${args.pattern}"`;
  if (args.command) return args.command.length > 80 ? args.command.slice(0, 79) + "…" : args.command;
  if (args.name) return args.name;
  if (args.query) return `"${args.query}"`;
  // Fallback: first scalar value
  for (const [k, v] of Object.entries(args)) {
    if (typeof v === "string" || typeof v === "number") {
      const s = String(v);
      return s.length > 60 ? s.slice(0, 59) + "…" : s;
    }
  }
  return "";
}

function fmtDuration(ms) {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

// --- pure helpers (no DOM — unit-tested in agent-activity.test.mjs) ----------

/**
 * Map a permission decision to its human status label. Single source of truth
 * for the resolved-card badge, shared by buildPermissionCard (replay) and
 * resolvePermission (live) so both render identical text.
 * @param {string} decision — "deny" | "allow_always" | "allow"
 * @returns {string}
 */
export function permissionDecisionLabel(decision) {
  if (decision === "deny") return "denied";
  if (decision === "allow_always") return "allowed (always)";
  return "allowed";
}

/**
 * Extract the assistant's answer prose for the copy-answer affordance (F3).
 * Prefers the streamed text parts (joined); falls back to the final summary when
 * the run streamed no prose. PURE (no DOM) — unit-testable in plain node.
 * @param {Array}  parts
 * @param {string} [summary]
 * @returns {string}
 */
export function answerTextFromParts(parts, summary) {
  const prose = (Array.isArray(parts) ? parts : [])
    .filter((p) => p && p.type === "text" && p.md)
    .map((p) => p.md)
    .join("\n\n")
    .trim();
  return prose || String(summary || "").trim();
}

// renderMarkdown and renderInline are imported from agent-parts-md.js above.

// --- public API ----------------------------------------------------------

export function ensureActivityShell(convId, bubble, onCancel) {
  let s = STATE.get(convId);
  if (s && s.process.isConnected) return s;
  // A cached shell whose process block is no longer in the document is stale: the
  // transcript was rebuilt underneath it (a history restore wipes the session
  // container), so rendering into it would be invisible. Rebuild on the
  // caller's bubble, carrying the run's start time and tool count so the
  // "Worked for …" line still reports the whole run, not just the tail.
  const carried = s ? { startedAt: s.startedAt, toolCount: s.toolCount } : null;
  if (s) STATE.delete(convId);

  let body = bubble.querySelector(":scope > .msg-prose");
  if (!body) {
    body = el("div", { class: "msg-prose" });
    bubble.appendChild(body);
  }

  // Process block (thinking + tool activity), collapsible. Header is the ONLY
  // top-level disclosure. Visible from creation (a fresh shell means a run is
  // starting) so the live label + Cancel are reachable even on a run with no
  // process content; _updateProcess hides it once there's an answer and no
  // process content, or once the run finalizes with no process content.
  const process = el("div", { class: "agent-process" });
  const processIcon = el("i", { class: "ic ic-sm ic-loader ic-spin" });
  const processLabel = el("span", { class: "process-label is-live" }, "Thinking…");
  const cancelLink = el("button", { class: "process-cancel", type: "button",
    title: "Cancel run", onclick: (e) => { e.stopPropagation(); onCancel(convId); } }, "Cancel");
  const chevron = el("i", { class: "ic ic-sm ic-chevron-down process-chevron" });
  const processHead = el("div", { class: "process-head", role: "button", tabindex: "0",
    "aria-expanded": "true" }, processIcon, processLabel, cancelLink, chevron);
  const processBody = el("div", { class: "process-body" });
  const toggle = () => {
    const open = process.classList.toggle("open");
    processHead.setAttribute("aria-expanded", String(open));
    s.userToggled = true;
  };
  processHead.addEventListener("click", toggle);
  processHead.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
  });
  process.append(processHead, processBody);
  process.classList.add("open");      // auto-open while streaming
  process.hidden = false;             // visible from the start of a streaming run (live label + Cancel)
  body.appendChild(process);

  // Answer block — primary, un-boxed prose. Sibling AFTER the process block.
  const answer = el("div", { class: "agent-answer" });
  body.appendChild(answer);

  s = {
    bubble, body,
    process, processHead, processIcon, processLabel, processBody, answer, cancelLink,
    userToggled: false, autoCollapsed: false,
    summary: null, actions: null,
    toolCalls: new Map(),
    startedAt: carried ? carried.startedAt : Date.now(),
    toolCount: carried ? carried.toolCount : 0,
  };
  STATE.set(convId, s);
  return s;
}

export function setStatus(convId, label) {
  const s = STATE.get(convId);
  if (!s) return;
  s.processLabel.textContent = label || "Thinking…";
}

// ---------------------------------------------------------------------------
// renderTurn — keyed differ for the parts model
// ---------------------------------------------------------------------------
// renderPart and updateTextNode are injected by main.js via initRenderTurn()
// to avoid a circular ES-module dependency:
//   agent-parts.js → agent-activity.js → agent-parts.js
// main.js imports both modules and wires them after the module graph is loaded.
let _renderPart = null;
let _updateTextNode = null;

/** Called once by main.js after all modules are loaded.
 *  @param {Function} renderPartFn   — renderPart from agent-parts.js
 *  @param {Function} [updateTextNodeFn] — updateTextNode from agent-parts.js
 */
export function initRenderTurn(renderPartFn, updateTextNodeFn) {
  _renderPart = renderPartFn;
  _updateTextNode = updateTextNodeFn || null;
}

// ---------------------------------------------------------------------------
// initResolveDispatch — inject the parts-model dispatcher (C4)
// ---------------------------------------------------------------------------
// When the user answers a permission/question card, the resolution must be
// recorded on the PART so it persists and REPLAYS as a non-interactive resolved
// card — not a live, clickable one that would POST conversation_id: undefined.
// agent-activity.js can't import applyAgentEvent from main.js (import cycle), so
// main.js injects a dispatcher (convId, ev) => applyAgentEvent(convId, ev), the
// same wiring pattern as initRenderTurn above.
let _resolveDispatch = null;

/** Called once by main.js after all modules are loaded.
 *  @param {Function} fn — (convId, ev) => void; folds ev into the parts model. */
export function initResolveDispatch(fn) {
  _resolveDispatch = fn;
}

// ---------------------------------------------------------------------------
// initRegenerate — inject the "regenerate this answer" action
// ---------------------------------------------------------------------------
// The Regenerate button lives on the finished assistant message (built in
// finalizeArtifacts below) but re-running a turn belongs to main.js (owns
// sendMessage / the run socket / the session messages model). agent-activity.js
// can't import main.js (import cycle), so main.js injects the handler — the same
// wiring pattern as initRenderTurn / initResolveDispatch above.
let _onRegenerate = null;

/** Called once by main.js after all modules are loaded.
 *  @param {Function} fn — (convId) => void; re-runs the preceding user prompt. */
export function initRegenerate(fn) {
  _onRegenerate = fn;
}

// ---------------------------------------------------------------------------
// groupForRender — pre-pass that collapses consecutive cheap read rows
// ---------------------------------------------------------------------------
/**
 * Collapse maximal runs of consecutive `tool` parts that are ALL:
 *   - risk === "read"
 *   - same verb
 *   - status === "ok"
 *   - no rich body (no result.diff, no result.traces)
 * into a single rollup render-item when the run length is ≥ 3.
 * Shorter runs remain as individual parts. Non-qualifying parts pass through.
 *
 * @param {Array} parts — raw parts[] from the parts reducer
 * @returns {Array}     — render items: either a part or a rollup descriptor
 *   { id:"rollup-<firstPartId>", type:"rollup", verb, count, parts:[] }
 */
export function groupForRender(parts) {
  const out = [];
  let i = 0;
  while (i < parts.length) {
    const p = parts[i];
    const isGroupable = (
      p.type === "tool" &&
      p.risk === "read" &&
      p.status === "ok" &&
      !(p.result && (p.result.diff || p.result.traces))
    );
    if (isGroupable) {
      // Extend the run as far as it stays groupable with the same verb.
      const run = [];
      let j = i;
      while (j < parts.length) {
        const q = parts[j];
        if (
          q.type === "tool" &&
          q.verb === p.verb &&
          q.risk === "read" &&
          q.status === "ok" &&
          !(q.result && (q.result.diff || q.result.traces))
        ) {
          run.push(q);
          j++;
        } else {
          break;
        }
      }
      if (run.length >= 3) {
        out.push({ id: `rollup-${p.id}`, type: "rollup", verb: p.verb, count: run.length, parts: run });
        i = j;
        continue;
      }
    }
    out.push(p);
    i++;
  }
  return out;
}

// ---------------------------------------------------------------------------
// splitTurnItems — document-order Process/Answer split
// ---------------------------------------------------------------------------
/**
 * Split grouped render items into the Process trail and the Answer.
 *
 * The old split was BY TYPE (every text part → Answer, everything else →
 * Process), which destroyed the run's document order: the narration
 * paragraphs ("Now let me check …") rendered as one long visible blob while
 * the tool calls they narrate hid together inside the Process block, so a
 * finished answer never showed HOW it was conducted (user report,
 * 2026-08-24). This splits BY POSITION instead: everything up to and
 * including the LAST non-text item is the Process trail — narration text and
 * tool cards interleaved exactly as the run happened — and only the text
 * AFTER the last tool/reasoning item is the Answer. A turn with no non-text
 * items has no Process block at all (plain text answer, unchanged).
 *
 * While streaming, the trailing text is the provisional Answer; when the
 * next tool starts, that text folds back into the trail on the next
 * renderTurn — the same reading Claude's own UI gives a live run.
 */
export function splitTurnItems(items) {
  let split = 0;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type !== "text") split = i + 1;
  }
  return { processItems: items.slice(0, split), answerItems: items.slice(split) };
}

// ---------------------------------------------------------------------------
// renderRollup — renders a collapsed row with an expand affordance
// ---------------------------------------------------------------------------
/**
 * Render a rollup descriptor into a single collapsible row.
 * The row shows the verb + count (e.g. "Read 4") and a chevron toggle.
 * Expanding reveals individual tool rows rendered via _renderPart.
 *
 * @param {{ id:string, type:"rollup", verb:string, count:number, parts:Array }} item
 * @returns {HTMLElement}
 */
export function renderRollup(item) {
  const wrap = el("div", { class: "agent-rollup", "data-part-id": item.id });

  // Head row: icon + "Verb N" label + expand chevron.
  const label = el("span", { class: "rollup-label" }, `${esc(item.verb || "Read")} ${item.count}`);
  const chevron = el("span", { class: "rollup-chevron" }, el("i", { class: "ic ic-sm ic-chevron-right" }));
  const head = el("button", {
    class: "rollup-head",
    type: "button",
    "aria-expanded": "false",
    title: `${item.count} ${esc(item.verb || "read")} calls — click to expand`,
  },
    el("i", { class: "ic ic-sm ic-list" }),
    label,
    chevron,
  );

  // Body: hidden list of individual tool rows, rendered lazily on first expand.
  const body = el("div", { class: "rollup-body" });
  body.hidden = true;
  let rendered = false;

  head.addEventListener("click", () => {
    const open = wrap.classList.toggle("open");
    head.setAttribute("aria-expanded", String(open));
    body.hidden = !open;
    // Lazy-render the nested tool rows on first expand.
    if (open && !rendered && _renderPart) {
      rendered = true;
      for (const p of item.parts) {
        body.appendChild(_renderPart(p));
      }
    }
  });

  wrap.appendChild(head);
  wrap.appendChild(body);
  return wrap;
}

// ---------------------------------------------------------------------------
// renderSig — lightweight render signature for a render item
// ---------------------------------------------------------------------------
// Used by renderTurn to detect whether a non-text existing node needs
// replacement. The signature encodes the fields that affect the rendered
// output; when the signature is unchanged the existing node is left in place
// (preserving any expanded inner state). When it changes (e.g. status flips
// from "running" to "ok" and durationMs becomes non-null on tool_result) the
// node is replaced so the new content (including the formatted duration) is
// shown.
//
// Rollup signature: just the count — only changes when the run length changes.
// Tool signature:   status | durationMs | presence of result/traces/diff
// Plan signature:   todo count + a hash of each todo's text+status, so live
//                   TodoWrite progress (a checkbox advancing) re-renders.
// Reasoning:        streaming flag | durationMs | md length, so streaming
//                   reasoning text re-renders as it grows.
// Other parts:      a stable prefix (they are not subject to in-place patching).

// Compact non-cryptographic string hash (djb2) — folds variable-length content
// (plan todos) into a short, comparison-stable token for a render signature.
function _sigHash(s) {
  const str = String(s ?? "");
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function renderSig(item) {
  if (item.type === "rollup") {
    return `rollup|${item.count}`;
  }
  if (item.type === "tool") {
    const r = item.result;
    const flags = r
      ? `r${r.traces ? "t" : ""}${r.diff ? "d" : ""}${r.diffStats ? "s" : ""}`
      : "";
    return `tool|${item.status}|${item.durationMs ?? ""}|${flags}`;
  }
  if (item.type === "plan") {
    // Hash the todos' text+status so the signature changes when a todo advances
    // (pending → in_progress → completed) or its text changes — otherwise the
    // plan froze at first paint because the signature was a constant.
    const todos = Array.isArray(item.todos) ? item.todos : [];
    const serial = todos.map((t) => `${t.status || ""}:${t.content || ""}`).join("␟");
    return `plan|${todos.length}|${_sigHash(serial)}`;
  }
  if (item.type === "reasoning") {
    // Include the reasoning md length so the signature advances as text streams
    // in — a streaming reasoning part (durationMs null) otherwise had a constant
    // "reasoning|s|" signature and froze at first paint.
    return `reasoning|${item.streaming ? "s" : "f"}|${item.durationMs ?? ""}|${(item.md || "").length}`;
  }
  // C4: permission/question — include resolution so a resolve dispatch flips the
  // signature (live → resolved), forcing renderTurn to re-render the card into its
  // non-interactive resolved state. Historically these were a frozen "other|<type>",
  // so a resolved part never re-rendered and replay showed a live, clickable card.
  if (item.type === "permission" || item.type === "question") {
    return `${item.type}|${item.resolution == null ? "live" : "resolved"}`;
  }
  // sanitizer + any other one-shot parts — replace only on first insert.
  return `other|${item.type}`;
}

// renderTurn — split render-items into the Process body (non-text) and the
// Answer block (text), diffed independently (see _diffPartsInto below) and
// keyed by data-part-id; then updates the Process header via _updateProcess.
// Preserves the .open (expanded) state across re-renders using a lightweight
// signature stored in node.dataset.sig — nodes whose signature has not
// changed are left untouched.

// Diff one ordered list of render-items into `container`, keyed by data-part-id
// (the exact reconcile the single-stream renderTurn used: rollup replace-on-count,
// text in-place update, sig-based replace preserving .open, append new, drop stale).
function _diffPartsInto(container, items, convId) {
  const seen = new Set();
  let cursor = null;
  for (const item of items) {
    seen.add(item.id);
    const existing = container.querySelector(`:scope > [data-part-id="${CSS.escape(item.id)}"]`);
    const sig = renderSig(item);
    if (item.type === "rollup") {
      const wasOpen = existing ? existing.classList.contains("open") : false;
      if (existing && existing.dataset.sig === sig) { cursor = existing; continue; }
      const node = renderRollup(item);
      node.dataset.sig = sig;
      if (wasOpen) node.classList.add("open");
      if (existing) container.replaceChild(node, existing);
      else if (cursor && cursor.nextSibling) container.insertBefore(node, cursor.nextSibling);
      else container.appendChild(node);
      cursor = node;
      continue;
    }
    if (existing) {
      if (item.type === "text" && _updateTextNode) { _updateTextNode(existing, item); cursor = existing; continue; }
      if (existing.dataset.sig === sig) { cursor = existing; continue; }
      const node = _renderPart(item, convId);
      node.dataset.sig = sig;
      if (existing.classList.contains("open")) node.classList.add("open");
      container.replaceChild(node, existing);
      cursor = node;
    } else {
      const node = _renderPart(item, convId);
      node.dataset.sig = sig;
      if (cursor && cursor.nextSibling) container.insertBefore(node, cursor.nextSibling);
      else container.appendChild(node);
      cursor = node;
    }
  }
  container.querySelectorAll(":scope > [data-part-id]").forEach((n) => {
    if (!seen.has(n.dataset.partId)) n.remove();
  });
}

// Compute the Process header label from the parts. `done` => past-tense + duration.
// Pure (duration is passed in, not computed here) so it serves BOTH the live
// ticking header (_updateProcess passes Date.now() - s.startedAt) and history
// replay (chat.js passes the persisted durationMs, or 0 when none was recorded).
export function processLabel(parts, durationMs, done) {
  const tools = parts.filter((p) => p.type === "tool");
  const nSteps = tools.length;
  const nFail = tools.filter((p) => p.status === "err").length;
  const hasReasoning = parts.some((p) => p.type === "reasoning");
  if (!done) {
    if (nSteps === 0) return "Thinking…";
    return `Working… · ${nSteps} ${nSteps === 1 ? "step" : "steps"}`;
  }
  const dur = fmtDuration(durationMs);
  let base;
  if (hasReasoning && nSteps > 0) base = `Thought for ${dur} · ${nSteps} ${nSteps === 1 ? "step" : "steps"}`;
  else if (hasReasoning)          base = `Thought for ${dur}`;
  else                            base = `Worked for ${dur} · ${nSteps} ${nSteps === 1 ? "step" : "steps"}`;
  if (nFail > 0) base += ` · ${nFail} failed`;
  return base;
}

// Show/hide the Process block; update its header label + streaming icon; auto-open
// while streaming and auto-collapse once the answer begins (unless the user toggled).
function _updateProcess(s, parts, hasProcess, hasAnswer, done) {
  // Visible while there's process content, OR during the pre-answer phase of any run (so the
  // live "Thinking…/Working…" label + Cancel show even on a status-only run). Once a pure
  // answer-only turn starts answering (no process content), hide it; and hide on finalize when
  // there is no process content.
  const show = hasProcess || (!done && !hasAnswer);
  s.process.hidden = !show;
  if (!show) return;
  // Once answering has started, thinking/working is done -- settle the header to its
  // past-tense form even before the run itself finalizes (done=true), so it doesn't
  // keep shimmering "Working…" while the answer is visibly streaming in below it.
  // When hasAnswer is false this is identical to the old `done`-only behavior.
  const settled = done || hasAnswer;
  s.processLabel.textContent = processLabel(parts, Date.now() - s.startedAt, settled);
  s.processLabel.classList.toggle("is-live", !settled);
  s.processIcon.className = settled ? "ic ic-sm ic-cpu" : "ic ic-sm ic-loader ic-spin";
  if (s.cancelLink) s.cancelLink.hidden = done;
  // An unresolved permission/question card blocks the run on user input. Left inside
  // a collapsed Process block it would be invisible and the run would look hung with
  // no way to unblock it -- force the block open and return before the userToggled
  // short-circuit below: a hidden blocking prompt is worse than overriding the user's
  // last toggle.
  const hasUnresolvedPrompt = parts.some(
    (p) => (p.type === "permission" || p.type === "question") && p.resolution == null);
  if (hasUnresolvedPrompt) {
    s.process.classList.add("open");
    s.processHead.setAttribute("aria-expanded", "true");
    return;
  }
  const anyErr = done && parts.some((p) => p.type === "tool" && p.status === "err");
  if (s.userToggled) return; // user's explicit choice wins for the rest of the turn
  // Errors stay expanded. Set aria-expanded here too (not just in the shared tail
  // below) — this branch returns early, and skipping it would leave aria-expanded
  // stuck at "false" from an earlier auto-collapse while the block is visually open.
  if (anyErr) { s.process.classList.add("open"); s.processHead.setAttribute("aria-expanded", "true"); return; }
  if (hasAnswer && !s.autoCollapsed) { s.process.classList.remove("open"); s.autoCollapsed = true; }
  else if (!hasAnswer && !s.autoCollapsed) { s.process.classList.add("open"); }
  s.processHead.setAttribute("aria-expanded", String(s.process.classList.contains("open")));
}

/**
 * @param {string} convId
 * @param {Array}  parts   — the current parts[] from the parts reducer
 */
export function renderTurn(convId, parts) {
  if (!_renderPart) return; // not yet wired — no-op until initRenderTurn() called
  const s = STATE.get(convId);
  if (!s) return;
  const renderItems = groupForRender(parts);
  const { processItems, answerItems } = splitTurnItems(renderItems);
  _diffPartsInto(s.processBody, processItems, convId);
  _diffPartsInto(s.answer, answerItems, convId);
  _updateProcess(s, parts, processItems.length > 0, answerItems.length > 0, false);
}

// Legacy per-event appender — superseded by the parts-model pipeline (main.js
// applyAgentEvent -> renderTurn/_diffPartsInto runs for every event; this
// function has no production caller). Kept only for direct-module-call test
// coverage, so appends fall back to s.processBody (there is no s.stream
// post-Process/Answer-split — see appendPermissionCard/appendQuestionCard below).
export function appendActivity(convId, kind, payload) {
  const s = STATE.get(convId);
  if (!s) return;

  if (kind === "tool_call") {
    s.toolCount++;
    const id = payload.id || `tc-${s.toolCount}`;
    const tool = payload.tool || "tool";
    const args = payload.args || {};
    const verb = humanizeVerb(tool);
    const targetText = activityTarget(tool, args);

    const row = el("div", { class: "activity-row activity-row-running",
                            "data-tool": shortToolName(tool) });
    const verbEl = el("span", { class: "activity-verb" }, verb);
    const targetEl = el("span", { class: "activity-target", title: targetText }, targetText);
    const checkEl = el("span", { class: "activity-check" }, ""); // populated on tool_result
    const head = el("button", { class: "activity-row-head", type: "button",
                                "aria-expanded": "false" },
      verbEl, targetEl, checkEl,
    );
    const detail = el("div", { class: "activity-row-detail" });
    detail.appendChild(el("div", { class: "activity-detail-label" }, "args"));
    detail.appendChild(el("pre", { class: "activity-detail-pre" }, JSON.stringify(args, null, 2)));

    head.addEventListener("click", () => {
      const open = row.classList.toggle("open");
      head.setAttribute("aria-expanded", String(open));
    });

    row.appendChild(head);
    row.appendChild(detail);
    s.processBody.appendChild(row);
    s.toolCalls.set(id, { row, detail, head, checkEl, tool, startedAt: Date.now() });
    return;
  }

  if (kind === "tool_result") {
    const id = payload.id;
    const entry = id ? s.toolCalls.get(id) : null;
    if (!entry) return;
    const ok = !!payload.ok;
    entry.checkEl.classList.remove("activity-check-ok", "activity-check-err");
    entry.row.classList.remove("activity-row-ok", "activity-row-err", "activity-row-running");
    entry.checkEl.textContent = "";
    entry.checkEl.appendChild(el("i", { class: `ic ic-sm ${ok ? "ic-check" : "ic-x"}` }));
    entry.checkEl.classList.add(ok ? "activity-check-ok" : "activity-check-err");
    entry.row.classList.add(ok ? "activity-row-ok" : "activity-row-err");
    if (payload.preview) {
      entry.detail.appendChild(el("div", { class: "activity-detail-label" }, "result"));
      entry.detail.appendChild(el("pre", { class: "activity-detail-pre" }, String(payload.preview).slice(0, 2000)));
    }
    return;
  }

  if (kind === "permission_request") {
    appendPermissionCard(convId, payload);
    return;
  }

  if (kind === "user_question") {
    appendQuestionCard(convId, payload);
    return;
  }

  if (kind === "file_read" || kind === "file_write") {
    const path = shortenPath(payload.path || "");
    const verb = kind === "file_read" ? "Read" : "Wrote";
    s.processBody.appendChild(el("div", { class: "activity-row activity-row-ok" },
      el("span", { class: "activity-verb" }, verb),
      el("span", { class: "activity-target", title: path }, path),
      el("span", { class: "activity-check activity-check-ok" },
        payload.diff_summary ? payload.diff_summary : el("i", { class: "ic ic-sm ic-check" })),
    ));
    return;
  }

  if (kind === "script_sanitized") {
    const aborted = !!payload.aborted;
    const summaryList = Array.isArray(payload.summary) ? payload.summary : [];
    const summaryText = summaryList.length ? summaryList.join(", ") : "no changes";
    const icon = aborted ? "⚠️" : "🧹";
    const verb = aborted ? "Sanitiser aborted push" : "Cleaned";
    const target = payload.target_name || "";

    const row = el("div", {
      class: "activity-row script-sanitized-row " + (aborted ? "activity-row-err" : "activity-row-ok"),
      "data-tool": "sanitizer",
    });
    const verbEl = el("span", { class: "activity-verb" }, verb);
    const targetEl = el("span", { class: "activity-target", title: target }, target);
    const summaryEl = el("span", { class: "sanitize-summary" }, summaryText);
    const toggle = el("button", { class: "sanitize-diff-toggle", type: "button",
      "aria-expanded": "false" }, "diff ▾");
    const iconEl = el("span", { class: "activity-check" }, icon);
    const head = el("div", { class: "activity-row-head sanitize-head" },
      iconEl, verbEl, targetEl, summaryEl, toggle);

    const detail = el("div", { class: "activity-row-detail sanitize-diff-body" });

    toggle.addEventListener("click", () => {
      const open = row.classList.toggle("open");
      toggle.setAttribute("aria-expanded", String(open));
      toggle.textContent = open ? "diff ▴" : "diff ▾";
      if (open && !detail.dataset.rendered) {
        detail.appendChild(renderSanitizeDiff(payload.original || "", payload.cleaned || ""));
        detail.dataset.rendered = "1";
      }
    });

    row.appendChild(head);
    row.appendChild(detail);
    s.processBody.appendChild(row);
    return;
  }
}

// Permission request — interactive Allow once / Always / Deny card
//
// buildPermissionCard(part, convId) → HTMLElement
//   Pure builder: returns the card element without appending it.
//   When part.resolution != null the card renders in its resolved/disabled state.
//   convId is optional (omit for history replay where no live conversation exists).
export function buildPermissionCard(part, convId) {
  const reqId = part.id || part.request_id;
  const tool = part.tool || "tool";
  const input = part.input || {};
  const argSummary = summarizeArgs(tool, input);

  // data-part-id lets renderTurn find + replace this card in place when its
  // resolution changes (live → resolved); without it the diff can't match the
  // node and would leave the interactive card on screen after it was answered.
  const card = el("div", { class: "tool-card tool-card-permission", "data-part-id": reqId, "data-tool": shortToolName(tool), "data-permission-req": reqId });
  const head = el("div", { class: "tool-card-head" },
    el("span", { class: "tool-icon" }, el("i", { class: "ic ic-lock" })),
    el("span", { class: "tool-label" },
      el("span", { class: "tool-name" }, `Permission needed: ${shortToolName(tool)}`),
      argSummary ? el("span", { class: "tool-args", title: argSummary }, argSummary) : null,
    ),
    el("span", { class: "tool-status tool-status-pending" }, "waiting"),
  );

  const body = el("div", { class: "tool-card-body permission-body" });
  body.appendChild(el("div", { class: "tool-detail-label" }, "args"));
  body.appendChild(el("pre", { class: "tool-detail-pre" }, JSON.stringify(input, null, 2)));

  if (part.resolution != null) {
    // Resolved state — show decision, disable all buttons
    const decision = part.resolution;
    const statusEl = head.querySelector(".tool-status");
    if (statusEl) {
      statusEl.classList.remove("tool-status-pending");
      statusEl.classList.add(decision === "deny" ? "tool-status-err" : "tool-status-ok");
      statusEl.textContent = permissionDecisionLabel(decision);
    }
    card.classList.remove("tool-card-permission");
    card.classList.add(decision === "deny" ? "tool-card-err" : "tool-card-ok");
    // Don't expand resolved cards — they're historical.
  } else {
    // Live interactive state
    const reasonInput = document.createElement("input");
    reasonInput.type = "text";
    reasonInput.className = "perm-deny-reason";
    reasonInput.placeholder = "Reason (optional, sent to the agent)";
    body.appendChild(el("div", { class: "permission-actions" },
      el("button", {
        class: "action-btn action-btn-primary", type: "button",
        onclick: () => resolvePermission(card, convId, reqId, "allow"),
      }, "Allow once"),
      el("button", {
        class: "action-btn action-btn-ghost", type: "button",
        title: `Skip prompt for ${shortToolName(tool)} for the rest of this run`,
        onclick: () => resolvePermission(card, convId, reqId, "allow_always"),
      }, "Allow always"),
      el("button", {
        class: "action-btn action-btn-danger", type: "button",
        onclick: () => resolvePermission(card, convId, reqId, "deny"),
      }, "Deny"),
      reasonInput,
    ));
    // Permission cards are expanded by default so the user can see args + buttons.
    card.classList.add("open");
  }

  card.appendChild(head);
  card.appendChild(body);
  return card;
}

function appendPermissionCard(convId, payload) {
  const s = STATE.get(convId);
  if (!s) return;
  const part = { id: payload.request_id, tool: payload.tool, input: payload.input, resolution: null };
  s.processBody.appendChild(buildPermissionCard(part, convId));
}

async function resolvePermission(card, convId, reqId, decision) {
  // Lock the buttons immediately so a double-click doesn't fire twice.
  card.querySelectorAll(".permission-actions button").forEach(b => b.disabled = true);
  const reasonEl = card.querySelector(".perm-deny-reason");
  const reason = decision === "deny" && reasonEl ? reasonEl.value.trim() : undefined;
  const statusEl = card.querySelector(".tool-status");
  if (statusEl) {
    statusEl.classList.remove("tool-status-pending");
    statusEl.classList.add(decision === "deny" ? "tool-status-err" : "tool-status-ok");
    statusEl.textContent = permissionDecisionLabel(decision);
  }
  card.classList.remove("tool-card-permission");
  card.classList.add(decision === "deny" ? "tool-card-err" : "tool-card-ok");
  // Hide the body now that the decision is recorded; user can re-expand if curious.
  card.classList.remove("open");
  // C4: record the resolution on the PART so it persists and replays as a
  // non-interactive resolved card (renderTurn re-renders on the sig flip).
  if (_resolveDispatch && convId) {
    _resolveDispatch(convId, { type: "resolve_permission", request_id: reqId, decision });
  }
  try {
    if (getAuthMode() === "hosted") {
      // Run socket: reply on the control channel. The sandbox's PermissionBridge keys off a simple
      // approved bool (allow / allow_always -> approved; deny -> declined). `reason` is desktop-only.
      wsSend({ type: "permission_reply", request_id: reqId, approved: decision !== "deny" });
    } else {
      const body = { conversation_id: convId, request_id: reqId, decision };
      if (reason !== undefined && reason !== "") body.reason = reason;
      await fetch("/api/agent/permission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    }
  } catch (e) {
    console.error("permission resolve failed", e);
  }
}

// AskUserQuestion — interactive card with one block per question. Each block
// has the question text, the header pill, and either radio buttons (single
// select) or checkboxes (multi-select) plus an optional "Other" free-text
// field. A single Submit button sends one combined answer string back to the
// backend, which feeds it to the agent via PreToolUse deny + reason.
//
// buildQuestionCard(part, convId) → HTMLElement
//   Pure builder: returns the card element without appending it.
//   When part.resolution != null the card renders in its resolved/disabled state.
//   convId is optional (omit for history replay where no live conversation exists).
export function buildQuestionCard(part, convId) {
  const reqId = part.id || part.request_id;
  const questions = Array.isArray(part.questions) ? part.questions : [];
  if (!questions.length) return el("div", {});

  const card = el("div", { class: "tool-card tool-card-question", "data-part-id": reqId, "data-question-req": reqId });
  const head = el("div", { class: "tool-card-head" },
    el("span", { class: "tool-icon" }, el("i", { class: "ic ic-help-circle" })),
    el("span", { class: "tool-label" },
      el("span", { class: "tool-name" }, questions.length > 1 ? `${questions.length} questions` : "Question"),
    ),
    el("span", { class: "tool-status tool-status-pending" }, "waiting"),
  );

  const body = el("div", { class: "tool-card-body question-body" });

  if (part.resolution != null) {
    // Resolved state — show answered badge, render questions as read-only text
    const statusEl = head.querySelector(".tool-status");
    if (statusEl) {
      statusEl.classList.remove("tool-status-pending");
      statusEl.classList.add("tool-status-ok");
      statusEl.textContent = "answered";
    }
    card.classList.remove("tool-card-question");
    card.classList.add("tool-card-ok");
    // Show the answer text if available
    if (typeof part.resolution === "string" && part.resolution) {
      body.appendChild(el("div", { class: "tool-detail-label" }, "answer"));
      body.appendChild(el("pre", { class: "tool-detail-pre" }, part.resolution));
    }
    card.appendChild(head);
    card.appendChild(body);
    return card;
  }

  // Per-question selectors. Each question yields a {read: () => string}
  // accessor used to assemble the final answer.
  const readers = [];
  questions.forEach((q, qi) => {
    const block = el("div", { class: "question-block" });
    const header = q.header || `Q${qi + 1}`;
    if (header) {
      block.appendChild(el("div", { class: "question-header" }, header));
    }
    if (q.question) {
      block.appendChild(el("div", { class: "question-text" }, q.question));
    }

    const groupName = `q-${reqId}-${qi}`;
    const optionInputs = [];
    const otherText = el("input", { type: "text", class: "question-other-input",
                                    placeholder: "Type your own answer…" });
    otherText.style.display = "none";
    const options = Array.isArray(q.options) ? q.options : [];
    const multi = !!q.multiSelect;

    options.forEach((opt, oi) => {
      const inputId = `${groupName}-${oi}`;
      const input = el("input", {
        type: multi ? "checkbox" : "radio",
        name: groupName,
        id: inputId,
        value: opt.label || "",
      });
      optionInputs.push(input);
      const lbl = el("label", { for: inputId, class: "question-option" },
        input,
        el("span", { class: "question-option-label" }, opt.label || ""),
        opt.description ? el("span", { class: "question-option-desc" }, opt.description) : null,
      );
      block.appendChild(lbl);
    });

    // "Other" toggle — always offered so the user can answer free-form.
    const otherId = `${groupName}-other`;
    const otherInput = el("input", {
      type: multi ? "checkbox" : "radio",
      name: groupName,
      id: otherId,
      value: "__other__",
    });
    otherInput.addEventListener("change", () => {
      otherText.style.display = otherInput.checked ? "" : "none";
      if (otherInput.checked) otherText.focus();
    });
    const otherLbl = el("label", { for: otherId, class: "question-option question-option-other" },
      otherInput,
      el("span", { class: "question-option-label" }, "Other…"),
    );
    block.appendChild(otherLbl);
    block.appendChild(otherText);

    body.appendChild(block);

    readers.push(() => {
      const selected = [];
      for (const inp of optionInputs) {
        if (inp.checked) selected.push(inp.value);
      }
      if (otherInput.checked) {
        const v = otherText.value.trim();
        if (v) selected.push(v);
      }
      const answer = selected.join(", ");
      return { header, question: q.question || "", answer };
    });
  });

  const submitBtn = el("button", {
    class: "action-btn action-btn-primary", type: "button",
    onclick: () => resolveQuestion(card, convId, reqId, readers),
  }, "Submit");
  const skipBtn = el("button", {
    class: "action-btn action-btn-ghost", type: "button",
    title: "Skip — let the agent decide with its best judgment",
    onclick: () => resolveQuestionWithText(card, convId, reqId,
      "User skipped the question; proceed with your best judgment."),
  }, "Skip");
  body.appendChild(el("div", { class: "question-actions" }, submitBtn, skipBtn));

  card.appendChild(head);
  card.appendChild(body);
  card.classList.add("open");
  return card;
}

function appendQuestionCard(convId, payload) {
  const s = STATE.get(convId);
  if (!s) return;
  const part = { id: payload.request_id, questions: payload.questions, resolution: null };
  const card = buildQuestionCard(part, convId);
  if (card) s.processBody.appendChild(card);
}

async function resolveQuestion(card, convId, reqId, readers) {
  const parts = [];
  for (const read of readers) {
    const { header, question, answer } = read();
    if (!answer) {
      // At least one question is unanswered — flag and bail.
      const statusEl = card.querySelector(".tool-status");
      if (statusEl) {
        statusEl.classList.add("tool-status-err");
        statusEl.textContent = "answer required";
      }
      return;
    }
    const label = header || question || "answer";
    parts.push(`${label}: ${answer}`);
  }
  await resolveQuestionWithText(card, convId, reqId, parts.join("\n"));
}

async function resolveQuestionWithText(card, convId, reqId, answerText) {
  card.querySelectorAll(".question-actions button, .question-block input, .question-other-input")
    .forEach(b => b.disabled = true);
  const statusEl = card.querySelector(".tool-status");
  if (statusEl) {
    statusEl.classList.remove("tool-status-pending", "tool-status-err");
    statusEl.classList.add("tool-status-ok");
    statusEl.textContent = "answered";
  }
  card.classList.remove("tool-card-question");
  card.classList.add("tool-card-ok");
  card.classList.remove("open");
  // C4: record the answer on the PART so it persists and replays as a
  // non-interactive resolved card (renderTurn re-renders on the sig flip).
  if (_resolveDispatch && convId) {
    _resolveDispatch(convId, { type: "resolve_question", request_id: reqId, answer: answerText });
  }
  try {
    if (getAuthMode() === "hosted") {
      // Run socket: reply on the control channel (mirrors resolvePermission above). The
      // desktop /api/agent/answer-question route is gated in hosted mode, so this frame is
      // the only path that reaches the paused agent.
      wsSend({ type: "question_reply", request_id: reqId, answer: answerText });
    } else {
      await fetch("/api/agent/answer-question", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, request_id: reqId, answer: answerText }),
      });
    }
  } catch (e) {
    console.error("question resolve failed", e);
  }
}

// Legacy — superseded by the parts model's message_delta -> text-part path
// (renderTurn/_diffPartsInto into s.answer); no production caller. Kept
// crash-safe: falls back to s.answer (there is no s.stream post-split).
export function appendMessageDelta(convId, text) {
  const s = STATE.get(convId);
  if (!s || !text) return;
  // Inline streaming text — append into a paragraph at the end of the answer
  // (or merge into the last delta paragraph so contiguous chunks stay together).
  let last = s.answer.lastElementChild;
  if (!last || !last.classList.contains("agent-inline-text")) {
    last = el("div", { class: "agent-inline-text" });
    s.answer.appendChild(last);
  }
  last.appendChild(document.createTextNode(text));
}

// Side-by-side raw → cleaned diff for the script_sanitized activity card.
// Uses textContent (never innerHTML) so script source is never interpreted as
// markup and original whitespace/indentation is preserved verbatim.
function renderSanitizeDiff(original, cleaned) {
  const wrap = el("div", { class: "sanitize-diff" });
  const mk = (label, text, cls) => {
    const box = el("div", { class: "sanitize-diff-pane" });
    box.appendChild(el("div", { class: "activity-detail-label" }, label));
    const pre = el("pre", { class: "activity-detail-pre " + cls });
    pre.textContent = text || "(empty)";
    box.appendChild(pre);
    return box;
  };
  wrap.appendChild(mk("before (raw)", original, "sanitize-before"));
  wrap.appendChild(mk("after (pushed)", cleaned, "sanitize-after"));
  return wrap;
}

function renderDiffPreview(diffText, fullPath) {
  const wrap = el("div", { class: "changed-diff" });
  if (!diffText || typeof diffText !== "string") {
    wrap.appendChild(el("div", { class: "changed-diff-empty" }, "No diff preview available."));
    return wrap;
  }
  const lines = diffText.replace(/\n$/, "").split("\n");
  const MAX_LINES = 40;
  const shown = lines.slice(0, MAX_LINES);
  for (const ln of shown) {
    let cls = "diff-ctx";
    if (ln.startsWith("+") && !ln.startsWith("+++")) cls = "diff-add";
    else if (ln.startsWith("-") && !ln.startsWith("---")) cls = "diff-rem";
    wrap.appendChild(el("div", { class: `diff-line ${cls}` }, ln || " "));
  }
  if (lines.length > MAX_LINES) {
    const more = lines.length - MAX_LINES;
    const footer = el("button", { class: "diff-more", type: "button",
      onclick: () => { if (window.__openChatFile) window.__openChatFile(fullPath); } },
      `… ${more} more line${more === 1 ? "" : "s"} — open full file`);
    wrap.appendChild(footer);
  }
  return wrap;
}

function renderChangedFiles(artifacts, onPush) {
  const block = el("div", { class: "changed-files" });

  // Aggregate diff stats (when at least one artifact carries diff_stats).
  let totalAdded = 0, totalRemoved = 0, anyStats = false;
  for (const a of artifacts) {
    const st = a.diff_stats;
    if (st && typeof st === "object") {
      anyStats = true;
      totalAdded += Number(st.added) || 0;
      totalRemoved += Number(st.removed) || 0;
    }
  }

  const header = el("div", { class: "changed-header" });
  const title = el("span", { class: "changed-title" },
    `${artifacts.length} file${artifacts.length === 1 ? "" : "s"} changed`);
  header.appendChild(title);
  if (anyStats) {
    const stats = el("span", { class: "changed-stats" },
      el("span", { class: "stat-add" }, `+${totalAdded}`),
      " ",
      el("span", { class: "stat-rem" }, `−${totalRemoved}`));
    header.appendChild(stats);
  }
  const bulk = el("div", { class: "changed-bulk" });
  const discardAll = el("button", { class: "btn-ghost", type: "button" }, "Discard all");
  const pushAll = el("button", { class: "btn-primary", type: "button" }, "Push all to CPQ");
  bulk.append(discardAll, pushAll);
  header.appendChild(bulk);
  block.appendChild(header);

  // Per-file rows.
  const rowEntries = [];
  for (const art of artifacts) {
    const entry = renderChangedRow(art, onPush);
    rowEntries.push(entry);
    block.appendChild(entry.row);
  }

  discardAll.addEventListener("click", () => {
    for (const e of rowEntries) e.discard();
  });
  pushAll.addEventListener("click", async () => {
    pushAll.disabled = true; discardAll.disabled = true;
    try {
      for (const e of rowEntries) {
        if (e.isPending()) await e.push();
      }
    } finally {
      if (rowEntries.some(e => e.isPending())) {
        pushAll.disabled = false; discardAll.disabled = false;
      }
    }
  });

  return block;
}

function renderChangedRow(art, onPush) {
  const path = (art.args && art.args.path) || "";
  const rawKind = art.kind || "modified";
  const kind = rawKind.replace(/[^a-z0-9-]/gi, "-");
  const stats = art.diff_stats;
  const hasDiff = typeof art.diff === "string" && art.diff.length > 0;

  const row = el("details", { class: `changed-file changed-file-${kind}` });
  const summary = el("summary", { class: "changed-summary" });

  const pathEl = el("span", { class: "changed-path", title: path,
    onclick: (e) => { e.preventDefault(); e.stopPropagation();
                      if (window.__openChatFile) window.__openChatFile(path); } }, path);
  const kindEl = el("span", { class: `changed-kind changed-kind-${kind}` },
    kind === "new" ? "new file" : "modified");
  const statsEl = stats
    ? el("span", { class: "changed-row-stats" },
        el("span", { class: "stat-add" }, `+${stats.added || 0}`),
        " ",
        el("span", { class: "stat-rem" }, `−${stats.removed || 0}`))
    : el("span", { class: "changed-row-stats" });

  const discardBtn = el("button", { class: "btn-ghost btn-icon", type: "button",
    title: "Discard",
    onclick: (e) => { e.preventDefault(); e.stopPropagation(); discard(); }, html: '<i class="ic ic-sm ic-x"></i>' });
  const pushBtn = el("button", { class: "btn-primary btn-push", type: "button",
    onclick: (e) => { e.preventDefault(); e.stopPropagation(); push(); } }, "Push");
  const actions = el("div", { class: "changed-actions" }, discardBtn, pushBtn);

  summary.append(pathEl, kindEl, statsEl, actions);
  row.appendChild(summary);

  if (hasDiff) {
    row.appendChild(renderDiffPreview(art.diff, path));
  } else {
    summary.style.cursor = "default";
    summary.addEventListener("click", (e) => e.preventDefault());
    row.classList.add("changed-file-no-diff");
  }

  let state = "pending"; // pending | pushing | pushed | discarded

  async function push() {
    if (state !== "pending") return;
    state = "pushing";
    pushBtn.disabled = true; discardBtn.disabled = true;
    pushBtn.textContent = "Pushing…";
    try {
      await onPush(art);
      pushBtn.textContent = "✓ Pushed";
      pushBtn.classList.add("btn-pushed");
      state = "pushed";
    } catch (e) {
      pushBtn.textContent = "Failed — Retry";
      pushBtn.disabled = false; discardBtn.disabled = false;
      state = "pending";
      console.error("push artifact failed", e);
    }
  }

  function discard() {
    if (state !== "pending") return;
    row.classList.add("changed-file-discarded");
    pushBtn.disabled = true; discardBtn.disabled = true;
    state = "discarded";
  }

  return {
    row, push, discard,
    isPending: () => state === "pending",
  };
}

/** Render the dock's run steps as a compact per-run transcript in the bubble.
 * The live tool stream renders ONCE (in the dock); on finalize we keep the
 * per-run record here so historical bubbles still show what the agent did. */
function _renderTranscript(steps) {
  const tool = (steps || []).filter((st) => st && st.verb);
  if (!tool.length) return null;
  const wrap = el("div", { class: "agent-transcript" });
  for (const st of tool) {
    const stat = st.status === "err" ? "err" : "ok";
    wrap.appendChild(el("div", { class: `agent-transcript-row ${stat}` },
      el("span", { class: "att-verb" }, st.verb || "Ran"),
      el("span", { class: "att-target", title: st.target || "" }, st.target || ""),
      (st.ms != null ? el("span", { class: "att-ms" }, fmtDuration(st.ms)) : null),
    ));
  }
  return wrap;
}

// Legacy dock-steps finalize (predates the parts model) — superseded in
// production by finalizeArtifacts below (main.js calls that on agent_final).
// Kept for direct-module-call test coverage (R1 dock/bubble dedup, the rewind
// change-chip). s.thinking no longer exists post-Process/Answer-split (the
// guard below is a no-op); the inline-text cleanup targets s.answer, the only
// place appendMessageDelta could have put one.
export function finalize(convId, payload, onPushArtifact, steps = []) {
  const s = STATE.get(convId);
  if (!s) return;

  // Remove the thinking line — the worked-for divider
  // and the summary prose take its place.
  if (s.thinking && s.thinking.parentNode) s.thinking.remove();

  // Tool transcript — live stream renders once in the dock; bubble keeps the per-run record.
  // Divider + transcript appear only when at least one tool ran.
  const toolSteps = (steps || []).filter((st) => st && st.verb);
  if (toolSteps.length > 0) {
    const dur = Date.now() - s.startedAt;
    const stepLabel = toolSteps.length === 1 ? "step" : "steps";
    const divider = el("div", { class: "agent-worked" },
      `Worked for ${fmtDuration(dur)} · ${toolSteps.length} ${stepLabel}`);
    s.body.appendChild(divider);
    const transcript = _renderTranscript(toolSteps);
    if (transcript) s.body.appendChild(transcript);
  }

  // The streaming text contains the same content as the final summary
  // (both come from the assistant's last text block). Drop streamed
  // inline-text divs so the markdown-rendered summary is authoritative.
  s.answer.querySelectorAll(".agent-inline-text").forEach(n => n.remove());

  const summaryHtml = renderMarkdown(payload.summary || "Done");
  s.summary = el("div", { class: "agent-summary md" });
  s.summary.innerHTML = summaryHtml;
  s.body.appendChild(s.summary);

  const artifacts = payload.artifacts || [];
  if (artifacts.length) {
    s.actions = renderChangedFiles(artifacts, (art) => onPushArtifact(convId, art));
    s.body.appendChild(s.actions);
  }

  // Rewind change-chip: show what this task changed + quick undo (best-effort)
  _renderChangeChip(convId, s.body);

  // Clean up active marker so future user msgs get a new bubble
  s.bubble.removeAttribute("data-active");
  STATE.delete(convId);
}

// ---------------------------------------------------------------------------
// finalizeArtifacts — parts-model finalize
// ---------------------------------------------------------------------------
// Used by the parts-model pipeline (main.js applyAgentEvent). The streamed
// text parts already hold the prose; this function finalizes the Process
// header (via _updateProcess, done=true — no separate "Worked for" divider),
// adds the changed-files block, the change-chip, and does the STATE/data-active
// cleanup. Does NOT re-render summary prose (unless the fallback below fires)
// or remove .agent-inline-text nodes.
//
// @param {string}   convId
// @param {object}   payload        — agent_final data (artifacts, etc.)
// @param {Function} onPushArtifact — (convId, art) => Promise
// @param {Array}    [parts=[]]     — the finalized parts array for step count

export function finalizeArtifacts(convId, payload, onPushArtifact, parts = []) {
  const s = STATE.get(convId);
  if (!s) return;

  // Finalize the Process header (past-tense label, stop the spinner, collapse
  // unless the user opened it or a tool failed); no separate "Worked for" divider.
  const { processItems, answerItems } = splitTurnItems(groupForRender(parts));
  // A tools-only run whose only prose is the summary-fallback rendered further below
  // still counts as "has an answer" here -- otherwise the Process block would finalize
  // expanded while the fallback answer renders collapsed beneath it.
  const hasAnswer = answerItems.length > 0 || shouldRenderSummaryFallback(parts, payload.summary);
  _updateProcess(s, parts, processItems.length > 0, hasAnswer, true);

  // Changed-files block.
  const artifacts = payload.artifacts || [];
  if (artifacts.length) {
    s.actions = renderChangedFiles(artifacts, (art) => onPushArtifact(convId, art));
    s.body.appendChild(s.actions);
  }

  // Answer fallback: if the run streamed zero text deltas, no prose was ever
  // rendered — mirror finalize() and render payload.summary so the bubble
  // isn't left with only the Process block and no answer.
  // Guarded so normal streamed runs (which already show their prose) are
  // unchanged and the answer is never double-rendered. (best-effort)
  if (shouldRenderSummaryFallback(parts, payload.summary)) {
    const summaryHtml = renderMarkdown(payload.summary);
    const summary = el("div", { class: "agent-summary md" });
    summary.innerHTML = summaryHtml;
    s.answer.appendChild(summary);
  }

  // Action row on the finished assistant message (Copy + Regenerate). Both share
  // the .agent-copy-btn styling (theme-aware; see shell-v2.css) so the row reads
  // as one restrained control cluster. The row is appended only if it has a button.
  const actionsRow = el("div", { class: "agent-msg-actions" });

  // F3: Copy-answer affordance — copies the answer prose (streamed text parts, or
  // the summary fallback) to the clipboard, with a brief "Copied" confirmation.
  // Clipboard is guarded (try/catch).
  const answerText = answerTextFromParts(parts, payload.summary);
  if (answerText) {
    const copyIcon = el("i", { class: "ic ic-sm ic-copy", "aria-hidden": "true" });
    const copyBtn = el("button",
      { class: "agent-copy-btn agent-icon-btn", type: "button", title: "Copy answer", "aria-label": "Copy answer" },
      copyIcon);
    let copyResetTimer = null;
    copyBtn.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(answerText);
        copyBtn.classList.add("is-copied");
        copyIcon.className = "ic ic-sm ic-check";
        if (copyResetTimer) clearTimeout(copyResetTimer);
        copyResetTimer = setTimeout(() => {
          copyBtn.classList.remove("is-copied");
          copyIcon.className = "ic ic-sm ic-copy";
        }, 1500);
      } catch (e) {
        console.error("copy answer failed", e);
      }
    });
    actionsRow.appendChild(copyBtn);
  }

  // Regenerate affordance — re-run the SAME preceding user prompt to produce a
  // fresh answer, dropping this (stale) answer. main.js injects the handler via
  // initRegenerate (module cycle); no button when unwired.
  if (typeof _onRegenerate === "function") {
    const regenBtn = el(
      "button",
      { class: "agent-copy-btn agent-regen-btn agent-icon-btn", type: "button",
        title: "Regenerate answer", "aria-label": "Regenerate answer" },
      el("i", { class: "ic ic-sm ic-refresh-cw", "aria-hidden": "true" }),
    );
    regenBtn.addEventListener("click", () => _onRegenerate(convId));
    actionsRow.appendChild(regenBtn);
  }

  if (actionsRow.childNodes.length) s.body.appendChild(actionsRow);

  // Rewind change-chip (best-effort).
  _renderChangeChip(convId, s.body);

  // Clean up active marker.
  s.bubble.removeAttribute("data-active");
  STATE.delete(convId);
}

// ---------------------------------------------------------------------------
// Rewind change-chip — appended after summary in finalize() (best-effort)
// ---------------------------------------------------------------------------

async function _renderChangeChip(convId, body) {
  try {
    const { api } = await import("./api.js");
    const { openUndoDialog } = await import("./rewind-dialog.js");
    const { refreshTimeline } = await import("./timeline.js");
    const { normalizeAudit } = await import("./audit-model.js");
    let g;
    if (getAuthMode() === "hosted") {
      // Hosted: the desktop rewind_timeline tool route is gated; the per-connection audit
      // list carries the same journal entries. Scope server-side to this conversation
      // (hosted undo already keys its journal groups by conversation id, so group_id ===
      // convId keeps the Undo hook unchanged) and normalize through the shared model
      // (audit-model.js) so the chip's shape matches the Audit view's own grouping.
      const raw = await api.auditEntries({ conversation_id: convId });
      const { groups } = normalizeAudit(raw, "hosted", {});
      g = groups.find((x) => x.id === convId);
    } else {
      const raw = await api.rewindTimeline();
      const { groups } = normalizeAudit(raw, "local", {});
      g = groups.find((x) => x.id === convId);
    }
    if (!g || !g.entries.length) return;
    const r = { reversible: 0, risky: 0, irreversible: 0 };
    for (const e of g.entries) if (e.reversibility in r) r[e.reversibility] += 1;
    const chip = el("div", { class: "rw-chip" },
      el("span", { class: "rw-chip-lead" },
        el("i", { class: "ic ic-rewind" }),
        ` ${g.entries.length} ${g.entries.length === 1 ? "change" : "changes"}`),
      (r.reversible ? el("span", { class: "rev-badge rev-ok" },
        el("span", { class: "rev-dot" }),
        `${r.reversible} reversible`) : null),
      (r.risky ? el("span", { class: "rev-badge rev-risk" },
        el("span", { class: "rev-dot" }),
        `${r.risky} risky`) : null),
      el("span", { class: "rw-chip-sp" },
        el("button", {
          class: "btn btn-xs rw-chip-undo",
          onclick: () => { openUndoDialog({ group_id: convId }, refreshTimeline); },
        },
          el("i", { class: "ic ic-sm ic-undo" }),
          " Undo task")));
    body.appendChild(chip);
  } catch (_e) { /* best-effort: no chip on error */ }
}

// ---------------------------------------------------------------------------
// renderRoutingChip — "Working in: <domain>" chip at the top of the bubble
// ---------------------------------------------------------------------------
// Idempotent: if a .routing-chip already exists for this conversation it is
// updated in-place rather than duplicated.  Attaches at the TOP of the
// activity-shell body so it is always the first visible element.
export function renderRoutingChip(convId, domain) {
  try {
    const s = STATE.get(convId);
    if (!s) return;
    const body = s.body;
    if (!body) return;
    let chip = body.querySelector(".routing-chip");
    if (chip) {
      const label = chip.querySelector(".routing-chip-label");
      if (label) label.textContent = `Working in: ${domain}`;
      return;
    }
    chip = el("div", { class: "routing-chip" },
      el("i", { class: "ic ic-sm ic-layers" }),
      el("span", { class: "routing-chip-label" }, `Working in: ${domain}`),
    );
    // Prepend so it appears before the Process block and the answer
    body.insertBefore(chip, body.firstChild);
  } catch (_e) { /* best-effort: no chip on error */ }
}

export function showError(convId, message, onRetry, statusLabel = "Failed") {
  const s = STATE.get(convId);
  if (!s) {
    const log = document.getElementById("chatStream") || document.body;
    log.appendChild(el("div", { class: "agent-error-banner" }, message));
    return;
  }
  if (s.thinking && s.thinking.parentNode) s.thinking.remove();
  const banner = el("div", { class: "agent-error-banner" }, message);
  const retry = el("button", { class: "action-btn action-btn-ghost", type: "button",
    onclick: () => onRetry(convId) }, "Retry");
  const errBox = el("div", { class: "agent-error-box" }, banner, retry);
  s.body.appendChild(errBox);
  // Finalize the Process header so a run error doesn't leave it spinning forever:
  // stop the live spinner/shimmer, retitle the header to how the run actually ended
  // (a settled spinner next to a stale "Starting the agent…" reads as a hang), hide
  // Cancel (there's no run left to cancel), and force the block open as the error
  // resting state so the process content that led to the failure stays visible. The
  // label write MUST happen here: this function ends by deleting the conversation's
  // state entry, so no later setStatus() can reach the header. Each field is guarded
  // individually since a shell built by an older code path may not carry all of them.
  if (s.processIcon) s.processIcon.className = "ic ic-sm ic-cpu";
  if (s.processLabel) s.processLabel.textContent = statusLabel;
  if (s.processLabel) s.processLabel.classList.remove("is-live");
  if (s.cancelLink) s.cancelLink.hidden = true;
  if (s.process) s.process.classList.add("open");
  if (s.processHead) s.processHead.setAttribute("aria-expanded", "true");
  s.bubble.removeAttribute("data-active");
  STATE.delete(convId);
}
